import { useFormikContext } from 'formik';
import { useSearchKeywordSegmentFields } from './useSearchKeywordSegmentFields';
import { useCallback, useMemo } from 'react';
import { validateEmpty, validateMinMax } from 'utils/ValidateUtils';
import { createSelectOptionsFromEnum } from 'utils/SelectOptionsUtils';
import { ECSegmentFormData, ECSegmentType, EventType, MAX_SEGMENT_LIFETIME } from 'core/ecSegment/ECSegment';
import styles from '../ecSegmentForm.module.scss';
import i18n from 'i18n';
import { FormConfig } from 'components/form/FormConfig';
import { SelectOptions } from 'components/commonType';
import { camelCase } from 'lodash';
import { useProductCategorySegmentFields } from './useProductCategorySegmentFields';
import { useProductSegmentFields } from './useProductSegmentFields';

const segmentTypeOptions: SelectOptions[] = createSelectOptionsFromEnum(
  ECSegmentType,
  'ecSegment.types.',
  [ECSegmentType.INTERSECT],
  (value) => camelCase(value)
);

const useSegmentFieldsOfType = (type: ECSegmentType) => {
  const searchKeywordsFields = useSearchKeywordSegmentFields(type);
  const { loading, fields: productCategoryFields } = useProductCategorySegmentFields(type);
  const productFields = useProductSegmentFields(type);

  return {
    loading,
    fields: [
      ...searchKeywordsFields,
      ...productCategoryFields,
      ...productFields
    ]
  };
};

export const useCreateECSegmentFields = (
  advertiserOptions: SelectOptions[],
  onAdvertiserChange: (advertiserId: number) => void
) => {
  const { values, setFieldValue } = useFormikContext<ECSegmentFormData>();
  const { type } = values;
  const { loading, fields: fieldsOfType } = useSegmentFieldsOfType(type);

  const onTypeChange = useCallback((type) => {
    setFieldValue('inclusion', []);
    if (type === ECSegmentType.SEARCH_KEYWORDS) {
      setFieldValue('eventType', EventType.SEARCH);
    } else {
      setFieldValue('eventType', EventType.VIEW_CONTENT);
    }
    if (type !== ECSegmentType.PRODUCT_CATEGORY) {
      setFieldValue('vendorNumber', undefined);
    }
  }, [setFieldValue]);

  const fields = useMemo(() => {
    const basicFields = new FormConfig.FieldsBuilder()
      .addFormikSelect({
        name: 'advertiserId',
        label: i18n.t<string>('ecSegmentForm.labels.advertiser'),
        options: advertiserOptions,
        simpleValue: true,
        validate: validateEmpty,
        onChange: onAdvertiserChange
      })
      .addFormikInput({
        name: 'name',
        label: i18n.t<string>('ecSegmentForm.labels.name'),
        placeholder: i18n.t<string>('ecSegmentForm.placeholders.name'),
        validate: validateEmpty
      })
      .addFormikInputGroup({
        name: 'durationDays',
        label: i18n.t<string>('ecSegmentForm.labels.durationDays'),
        prefix: i18n.t<string>('ecSegmentForm.labels.durationDaysPrefix'),
        postfix: i18n.t<string>('ecSegmentForm.labels.durationDaysPostfix'),
        type: 'number',
        className: styles.durationInput,
        fieldContentWidth: 'auto',
        validate: (value) => validateMinMax(value, 1, MAX_SEGMENT_LIFETIME)
      })
      .addFormikSelect({
        name: 'type',
        label: i18n.t<string>('ecSegmentForm.labels.type'),
        options: segmentTypeOptions,
        simpleValue: true,
        validate: validateEmpty,
        onChange: onTypeChange
      })
      .build();

    return [
      ...basicFields,
      ...fieldsOfType
    ];
  }, [
    advertiserOptions,
    fieldsOfType,
    onTypeChange,
    onAdvertiserChange
  ]);

  return {
    loading,
    fields
  };
};

export const useEditECSegmentFields = (
  advertiserOptions: SelectOptions[]
) => {
  const { values } = useFormikContext<ECSegmentFormData>();
  const { type } = values;
  const { loading, fields: fieldsOfType } = useSegmentFieldsOfType(type);

  const fields = useMemo(() => {
    const basicFields = new FormConfig.FieldsBuilder()
      .addFormikLabel({
        name: 'advertiserId',
        label: i18n.t<string>('ecSegmentForm.labels.advertiser'),
        formatter: (value: string) => {
          const advertiser = advertiserOptions.find((option) => option.value === value);
          return advertiser ? advertiser.label : value.toString();
        }
      })
      .addFormikInput({
        name: 'name',
        label: i18n.t<string>('ecSegmentForm.labels.name'),
        placeholder: i18n.t<string>('ecSegmentForm.placeholders.name'),
        validate: validateEmpty
      })
      .addFormikInputGroup({
        name: 'durationDays',
        label: i18n.t<string>('ecSegmentForm.labels.durationDays'),
        prefix: i18n.t<string>('ecSegmentForm.labels.durationDaysPrefix'),
        postfix: i18n.t<string>('ecSegmentForm.labels.durationDaysPostfix'),
        type: 'number',
        className: styles.durationInput,
        fieldContentWidth: 'auto',
        validate: (value) => validateMinMax(value, 1, MAX_SEGMENT_LIFETIME)
      })
      .addFormikLabel({
        name: 'type',
        label: i18n.t<string>('ecSegmentForm.labels.type'),
        formatter: (value: string) => i18n.t<string>(`ecSegment.types.${camelCase(value)}`)
      })
      .build();

    return [
      ...basicFields,
      ...fieldsOfType
    ];
  }, [
    advertiserOptions,
    fieldsOfType
  ]);

  return {
    loading,
    fields
  };
};
